/**
 * @module engine/input/KeyboardController
 * @summary Document-level keyboard shortcuts.
 * @description Guards against firing while typing in inputs/textareas (so panel
 *   editing isn't hijacked). Attaches to `document`; detaches on unmount.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3659-3692.
 */
import { S, emit, markDirty } from '../state';
import { toS } from '../coords';
import { undo, redo } from '../history';
import { pushUndo } from '../history';
import { scheduleAutoSave } from '../persistence';
import { doCopy, doPaste, doDelete, doDuplicate, selectAll } from '../ops/editOps';
import { groupSelected, ungroupSelected, rotateSelected } from '../ops/transformOps';
import { toast } from '../../lib/toast';
import type { Tool, Shape } from '../../types';

export interface KeyboardCallbacks {
  setTool: (t: Tool) => void;
  requestSave: () => void;
  openLabelEdit: (shape: Shape, sx: number, sy: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;
  fit: () => void;
}

// While a full-screen overlay (Symbols, Users, Help, …) is open the canvas is
// hidden but stays mounted — canvas shortcuts must not fire "invisibly".
let suspended = false;
export function setShortcutsSuspended(v: boolean): void {
  suspended = v;
}

/** Attach keyboard shortcuts to `document`. Returns a detach fn. */
export function attachKeyboardControls(cb: KeyboardCallbacks): () => void {
  // Coalesce a burst of arrow-key nudges (key repeat) into ONE undo snapshot.
  let lastNudge = 0;

  function onKeyDown(e: KeyboardEvent): void {
    if (suspended) return;
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;

    if (e.key === ' ') {
      S.spaceDown = true;
      e.preventDefault();
    }
    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase();
      if (k === 'z') {
        if (e.shiftKey) redo();
        else undo();
        e.preventDefault();
      }
      if (k === 'y') {
        redo();
        e.preventDefault();
      }
      if (k === '=' || k === '+') {
        cb.zoomIn();
        e.preventDefault();
      }
      if (k === '-' || k === '_') {
        cb.zoomOut();
        e.preventDefault();
      }
      if (k === '0') {
        cb.zoomReset();
        e.preventDefault();
      }
      if (k === 'c') {
        doCopy();
        e.preventDefault();
      }
      if (k === 'v') {
        doPaste();
        e.preventDefault();
      }
      if (k === 'd') {
        doDuplicate();
        e.preventDefault();
      }
      if (k === 'a') {
        selectAll();
        e.preventDefault();
      }
      if (k === 's') {
        cb.requestSave();
        e.preventDefault();
        return;
      }
      if (k === 'g' && e.shiftKey) {
        ungroupSelected();
        e.preventDefault();
        return;
      }
      if (k === 'g') {
        groupSelected();
        e.preventDefault();
        return;
      }
      return;
    }

    if (e.key === 'Escape' || e.key.toLowerCase() === 'v') cb.setTool('select');
    if (e.key.toLowerCase() === 'c' && !e.ctrlKey) cb.setTool('connect');
    if (e.key.toLowerCase() === 'h') cb.setTool('pan');
    if (e.key.toLowerCase() === 'f') {
      cb.fit();
      e.preventDefault();
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      doDelete();
      e.preventDefault();
    }
    const nud = e.shiftKey ? 20 : 1;
    if (e.key === 'F2' && S.selected.length) {
      const s = S.shapes.find((x) => x.id === S.selected[0]);
      if (s) {
        const sp = toS(s.x + s.w / 2, s.y + s.h + 2);
        cb.openLabelEdit(s, sp.x, sp.y);
      }
      e.preventDefault();
      return;
    }
    if (
      ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) &&
      S.selected.length
    ) {
      const now = Date.now();
      if (now - lastNudge > 500) pushUndo();
      lastNudge = now;
      S.selected.forEach((id) => {
        const s = S.shapes.find((x) => x.id === id);
        if (!s || s.locked) return;
        if (e.key === 'ArrowLeft') s.x -= nud;
        if (e.key === 'ArrowRight') s.x += nud;
        if (e.key === 'ArrowUp') s.y -= nud;
        if (e.key === 'ArrowDown') s.y += nud;
      });
      scheduleAutoSave();
      markDirty();
      emit('selection');
      e.preventDefault();
    }
    if (e.key.toLowerCase() === 'r' && S.selected.length) {
      rotateSelected(e.shiftKey ? -90 : 90);
      e.preventDefault();
    }
    if (e.key.toLowerCase() === 'l' && S.selected.length) {
      pushUndo();
      S.selected.forEach((id) => {
        const s = S.shapes.find((x) => x.id === id);
        if (s) s.locked = !s.locked;
      });
      markDirty();
      toast('Lock toggled');
    }
  }

  function onKeyUp(e: KeyboardEvent): void {
    if (e.key === ' ') S.spaceDown = false;
  }

  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);
  return () => {
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keyup', onKeyUp);
  };
}
